import React, { useState } from 'react';
import { Body, Button, Flex, Heading, Input, Loader } from '@cognite/cogs.js';
import { DigitalTwin } from 'types/dt-graphql-apollo-v2';
import styled from 'styled-components/macro';
import { CollectionCards } from './components/CollectionCards';
import { CollectionHeader } from './components/CollectionHeader';
import { CollectionInfoPanel } from './components/CollectionInfoPanel';
import { ContentHeader } from './components/ContentHeader';
import { SchemaFilterList, ViewOption } from './components/SchemaFilterList';
import { ComparisonRuleList } from './components/ComparisonRuleList';
import { NewCollectionModal } from './components/NewCollectionModal';
import { EditCollectionModal } from './components/EditCollectionModal';
import { EditSchemaFilterDrawer } from './components/EditSchemaFilterDrawer';
import { EditConsistencyRuleDrawer } from './components/EditConsistencyRuleDrawer';
import { NewConsistencySchemaFilterDrawer } from './NewConsistencySchemaFilterDrawer';
import { NewConsistencyRuleDrawer } from './NewConsistencyRuleDrawer';
import { useDataConsistencyPage } from './useDataConsistencyPage';
import { isConsistencyRuleDisabled } from './helpers';
import { RunConsistencyAttributesSelection } from './components/RunConsistencyAttributesSelection';

export const DataConsistency = ({
  digitalTwin,
}: {
  digitalTwin: DigitalTwin;
}) => {
  const {
    collections,
    selectedCollection,
    selectedCollectionId,
    search,
    loading,
    error,
    collectionSchemaFilters,
    filteredSchemaFilters,
    filteredComparisonRules,
    setSelectedCollectionId,
    setSearch,
    createCollection,
    updateCollection,
    deleteCollection,
    deleteCollectionById,
    updateSchemaFilter,
    deleteSchemaFilter,
    deleteRule,
    toggleDisableRule,
    togglingDisableRuleId,
    showCollectionInfo,
    toggleCollectionInfo,
    viewOption,
    setViewOption,
    showNewCollectionModal,
    openNewCollectionModal,
    closeNewCollectionModal,
    showNewSchemaFilterDrawer,
    openNewSchemaFilterDrawer,
    closeNewSchemaFilterDrawer,
    showNewRuleDrawer,
    openNewRuleDrawer,
    closeNewRuleDrawer,
    openCloneRuleDrawer,
    cloningRuleId,
    showEditCollectionModal,
    openEditCollectionModal,
    closeEditCollectionModal,
    editingCollectionId,
    showEditSchemaFilterDrawer,
    openEditSchemaFilterDrawer,
    closeEditSchemaFilterDrawer,
    editingSchemaFilterId,
    showEditRuleDrawer,
    openEditRuleDrawer,
    closeEditRuleDrawer,
    editingRuleId,
    comparisonRules,
    generateReport,
    runSingleRule,
    isGeneratingReport,
    schemas,
  } = useDataConsistencyPage(digitalTwin);

  const [showGenerateDialog, setShowGenerateDialog] = useState(false);

  const editingCollection = collections.find(
    (c) => c.externalId === editingCollectionId
  );
  const editingRule = comparisonRules.find(
    (r) => r.externalId === editingRuleId
  );
  const cloningRule = comparisonRules.find(
    (r) => r.externalId === cloningRuleId
  );
  const hasEnabledRules = comparisonRules.some(
    (r) => !isConsistencyRuleDisabled(r)
  );
  const editingSchemaFilter = collectionSchemaFilters.find(
    (sf) => sf.externalId === editingSchemaFilterId
  );

  if (loading) {
    return (
      <PageContainer
        direction="column"
        alignItems="center"
        justifyContent="center"
      >
        <Loader />
      </PageContainer>
    );
  }

  if (error) {
    return (
      <PageContainer
        direction="column"
        alignItems="center"
        justifyContent="center"
      >
        <ErrorMessage size="medium">
          Failed to load consistency collections. Please try again.
        </ErrorMessage>
      </PageContainer>
    );
  }

  return (
    <PageContainer direction="column">
      <CollectionHeader collectionCount={collections.length} />

      <CollectionCards
        collections={collections}
        selectedCollectionId={selectedCollectionId}
        onSelectCollection={setSelectedCollectionId}
        onCreateCollection={openNewCollectionModal}
        onEditCollection={openEditCollectionModal}
        onDeleteCollection={deleteCollectionById}
      />

      {selectedCollection && (
        <>
          <ContentHeader
            collection={{
              name: selectedCollection.name,
              schemaFiltersCount: collectionSchemaFilters.length,
              rulesCount: filteredComparisonRules.length,
            }}
            showCollectionInfo={showCollectionInfo}
            onToggleInfo={toggleCollectionInfo}
            onCreateSchemaFilter={openNewSchemaFilterDrawer}
            onCreateRule={openNewRuleDrawer}
            onEditCollection={() =>
              openEditCollectionModal(selectedCollection.externalId)
            }
            onDeleteCollection={deleteCollection}
            onGenerateReport={() => setShowGenerateDialog(true)}
            isGeneratingReport={isGeneratingReport}
            canGenerateReport={hasEnabledRules}
          />

          {showCollectionInfo && (
            <CollectionInfoPanel
              collection={{
                description: selectedCollection.description,
                createdAt: selectedCollection.createdAt,
                labels: selectedCollection.labels,
              }}
              schemaFilters={collectionSchemaFilters}
            />
          )}

          <SearchBar gap={8} alignItems="center">
            <StyledInput
              placeholder="Search schema filters and rules..."
              value={search}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setSearch(e.target.value)
              }
            />
            <Flex gap={4}>
              <Button
                icon="Grid"
                type={viewOption === ViewOption.Card ? 'primary' : 'ghost'}
                aria-label="Card view"
                onClick={() => setViewOption(ViewOption.Card)}
              />
              <Button
                icon="List"
                type={viewOption === ViewOption.List ? 'primary' : 'ghost'}
                aria-label="List view"
                onClick={() => setViewOption(ViewOption.List)}
              />
            </Flex>
          </SearchBar>

          <ContentArea direction="column" gap={24}>
            <section>
              <SectionHeading level={5}>Schema Filters</SectionHeading>
              <SchemaFilterList
                schemaFilters={filteredSchemaFilters}
                viewOption={viewOption}
                onEditSchemaFilter={openEditSchemaFilterDrawer}
                onDeleteSchemaFilter={deleteSchemaFilter}
              />
            </section>

            <section>
              <SectionHeading level={5}>Comparison Rules</SectionHeading>
              <ComparisonRuleList
                rules={filteredComparisonRules}
                onEditRule={openEditRuleDrawer}
                onCloneRule={openCloneRuleDrawer}
                onDeleteRule={deleteRule}
                onToggleDisableRule={toggleDisableRule}
                togglingDisableRuleId={togglingDisableRuleId}
                onRunRule={runSingleRule}
              />
            </section>
          </ContentArea>
        </>
      )}

      <NewCollectionModal
        visible={showNewCollectionModal}
        onClose={closeNewCollectionModal}
        onCollectionCreated={createCollection}
        digitalTwinId={digitalTwin.externalId}
      />

      <EditCollectionModal
        visible={showEditCollectionModal}
        onClose={closeEditCollectionModal}
        collection={editingCollection}
        onCollectionUpdated={updateCollection}
      />

      <EditSchemaFilterDrawer
        visible={showEditSchemaFilterDrawer}
        onClose={closeEditSchemaFilterDrawer}
        schemaFilter={editingSchemaFilter}
        onSchemaFilterUpdated={updateSchemaFilter}
        digitalTwin={digitalTwin}
      />

      <EditConsistencyRuleDrawer
        visible={showEditRuleDrawer}
        onClose={closeEditRuleDrawer}
        rule={editingRule}
        schemaFilters={collectionSchemaFilters}
        digitalTwin={digitalTwin}
      />

      <NewConsistencySchemaFilterDrawer
        visible={showNewSchemaFilterDrawer}
        onClose={closeNewSchemaFilterDrawer}
        collection={selectedCollection ?? null}
        digitalTwin={digitalTwin}
      />

      <NewConsistencyRuleDrawer
        visible={showNewRuleDrawer}
        onClose={closeNewRuleDrawer}
        collection={selectedCollection ?? null}
        schemaFilters={collectionSchemaFilters}
        digitalTwin={digitalTwin}
        initialRule={cloningRule}
      />

      {showGenerateDialog && (
        <RunConsistencyAttributesSelection
          key={selectedCollection?.externalId}
          visible={showGenerateDialog}
          loading={isGeneratingReport}
          collection={selectedCollection}
          onCancel={() => setShowGenerateDialog(false)}
          onCreateReport={(attributes) => {
            setShowGenerateDialog(false);
            generateReport(attributes);
          }}
          schemaFilters={collectionSchemaFilters}
          schemaData={schemas}
        />
      )}
    </PageContainer>
  );
};

const PageContainer = styled(Flex)`
  flex: 1;
  overflow: hidden;
`;

const SearchBar = styled(Flex)`
  padding: 0.75rem 2rem;
  border-bottom: 1px solid var(--cogs-bg-control--disabled);
`;

const StyledInput = styled(Input)`
  max-width: 320px;
`;

const ContentArea = styled(Flex)`
  flex: 1;
  overflow: auto;
  padding: 1rem 2rem;
`;

const SectionHeading = styled(Heading)`
  margin-bottom: 0.75rem;
`;

const ErrorMessage = styled(Body)`
  color: var(--cogs-text-danger);
`;
